import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'shared/contexts/I18nContext';
import { useLoading } from 'shared/contexts/Loading';
import ContentHeaderSection from '../../shared/common/ContentHeaderSection';
import apiCall from 'shared/services/ApiAdapter';
import { useMasterList } from 'shared/hooks/useMasterList';

/**
 * Displays students enrolled in a specific batch by fetching data from the backend.
 *
 * @param {object} props - The component props.
 * @param {function} props.navigateTo - Function to navigate to a different view.
 * @param {string} props.selectedBatchId - The ID of the currently selected batch.
 * @param {string} props.selectedCourseId - The ID of the course the batch belongs to.
 * @param {string} props.selectedCourseTitle - The title of the course for display.
 * @param {function} props.displayToast - Function to display toast notifications.
 */
const StudentRosterContent = ({ navigateTo, selectedBatchId, selectedCourseId, selectedCourseTitle, displayToast }) => {
    const [students, setStudents] = useState([]);
    const [batchName, setBatchName] = useState('');
    const { t } = useTranslation();
    const { startLoading, stopLoading, isLoading: componentLoading } = useLoading();

    // Use the master list hook to fetch curricula and academic classes
    const {
        masterData: { curricula, academicClasses },
        isLoading: masterDataLoading,
        error: masterDataError
    } = useMasterList({ displayToast, startLoading, stopLoading });

    // Helper function to get the label from the master list data
    const getLabel = useCallback((value, list) => {
        const item = list.find(i => i.value === value);
        return item ? item.label : value;
    }, []);

    const getStudentsAndBatchDetails = useCallback(async () => {
        if (selectedBatchId) {
            startLoading();
            try {
                // Fetch the batch details to get the batch name.
                const batchData = await apiCall(`/batches/${selectedBatchId}`, false, 'GET', null, startLoading, stopLoading, displayToast);
                setBatchName(batchData?.batchName || 'Selected Batch');

                // Fetch all enrollments for the specific batch.
                const enrollmentsData = await apiCall(`/batch-enrollments?batchId.equals=${selectedBatchId}`, false, 'GET', null, startLoading, stopLoading, displayToast);

                // Create an array of API call promises to fetch each student.
                const studentPromises = enrollmentsData.map(enrollment => {
                    const studentId = enrollment.student.id;
                    return apiCall(`/students/${studentId}`, false, 'GET', null, startLoading, stopLoading, displayToast);
                });

                // Use Promise.all to run all promises concurrently.
                const studentsInBatch = await Promise.all(studentPromises);

                setStudents(studentsInBatch);

            } catch (error) {
                console.error("Failed to fetch data for student roster:", error);
                displayToast(t('failedToLoadRoster'), 'error');
                setStudents([]);
                setBatchName('Selected Batch');
            } finally {
                stopLoading();
            }
        }
    }, [selectedBatchId, displayToast, startLoading, stopLoading, t]);

    useEffect(() => {
        getStudentsAndBatchDetails();
    }, [getStudentsAndBatchDetails]);

    // Combine loading states from both the component's API calls and the master list hook
    const isLoading = componentLoading || masterDataLoading;

    if (masterDataError) {
        return (
            <div className="p-8 md:px-10 text-center text-red-500">
                {masterDataError}
            </div>
        );
    }

    if (isLoading) {
        return <div className="p-8 md:px-10 text-center text-gray-600 dark:text-gray-400">{t('loadingStudentRoster')}</div>;
    }

    return (
        <div className="p-8 md:px-10">
            <ContentHeaderSection
                title={
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => navigateTo('myBatches', selectedCourseTitle, selectedCourseId)}
                            className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none"
                            aria-label="Go back to batches"
                        >
                            <svg className="w-6 h-6 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
                        </button>
                        <span className="text-2xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight leading-tight">
                            {t('studentRoster')} for {batchName}
                        </span>
                    </div>
                }
                subtitle={t('viewManageStudentRoster')}
                rightContentTop={null}
                rightContentBottom={null}
            />

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {students.length === 0 ? (
                    <div className="col-span-full text-center text-gray-600 dark:text-gray-400">{t('noStudentsInBatch')}</div>
                ) : (
                    students.map(student => (
                        <div key={student.id} className="flex flex-col items-start p-6 bg-white rounded-xl shadow-lg dark:bg-gray-800 gap-4">
                            <div className="flex items-center">
                                <img src={student.profilePictureUrl || `https://placehold.co/40x40/e0f2f7/00796b?text=${student?.name?.charAt(0)}`} alt={student.name} className="w-10 h-10 rounded-full mr-3" />
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 leading-tight">{student.name}</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Class: {getLabel(student.academicClass, academicClasses)}</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Curriculum: {getLabel(student.curriculum, curricula)}</p>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default StudentRosterContent;